import React from 'react';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  getComparisonSchema,
  getBreadcrumbSchema
} from '../../utils/seoSchemas';

const comparisonData = [
  { feature: 'Cloud-native (no installation)', goOps: true, competitor: false },
  { feature: 'Modern responsive UI', goOps: true, competitor: false },
  { feature: 'Mobile-first design', goOps: true, competitor: false },
  { feature: 'AI-powered email booking intake', goOps: true, competitor: false },
  { feature: 'Visual drag-and-drop job board', goOps: true, competitor: true },
  { feature: 'Digital vehicle health checks', goOps: true, competitor: true },
  { feature: 'Customer payment links', goOps: true, competitor: true },
  { feature: 'Automatic VAT-compliant invoicing', goOps: true, competitor: true },
  { feature: 'Book My Garage integration', goOps: true, competitor: false },
  { feature: 'Parts ordering (GSF, Euro Car Parts)', goOps: true, competitor: true },
  { feature: 'Real-time updates (WebSocket)', goOps: true, competitor: false },
  { feature: 'Works offline', goOps: false, competitor: true },
];

const TechMan = () => {
  const comparisonSchema = getComparisonSchema({
    competitor: 'TechMan',
    description: 'Compare Go Ops vs TechMan garage management software. Go Ops is cloud-native with a modern UI and mobile-first design.',
    url: 'https://www.go-ops.digital/compare/techman'
  });
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.go-ops.digital/' },
    { name: 'Compare', url: 'https://www.go-ops.digital/compare' },
    { name: 'Go Ops vs TechMan', url: 'https://www.go-ops.digital/compare/techman' }
  ]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet>
        <title>Go Ops vs TechMan | Garage Management Software Comparison</title>
        <meta
          name="description"
          content="Compare Go Ops vs TechMan garage management software. Go Ops is cloud-native with a modern UI, mobile-first design, and AI-powered automation."
        />
        <link rel="canonical" href="https://www.go-ops.digital/compare/techman" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Go Ops" />
        <meta property="og:title" content="Go Ops vs TechMan | Garage Management Software Comparison" />
        <meta
          property="og:description"
          content="Compare Go Ops vs TechMan. Go Ops is cloud-native with modern UI and mobile-first design."
        />
        <meta property="og:url" content="https://www.go-ops.digital/compare/techman" />
        <meta property="og:image" content="https://www.go-ops.digital/goopslogo.jpeg" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify(comparisonSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      {/* Navigation */}
      <Box sx={{ bgcolor: '#2c3e50', py: 2 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{ color: 'white', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Go Ops
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="text" component={RouterLink} to="/features" sx={{ color: 'white' }}>
                Features
              </Button>
              <Button variant="text" component={RouterLink} to="/pricing" sx={{ color: 'white' }}>
                Pricing
              </Button>
              <Button variant="text" component={RouterLink} to="/contact" sx={{ color: 'white' }}>
                Contact
              </Button>
              <Button
                variant="contained"
                component={RouterLink}
                to="/auth/login"
                sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
              >
                Login
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h1" component="h1" sx={{ fontSize: '2.5rem', fontWeight: 'bold', mb: 2 }}>
            Go Ops vs TechMan
          </Typography>
          <Typography variant="h2" component="p" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
            Cloud-native vs traditional garage management
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4, lineHeight: 1.8 }}>
          TechMan is a well-established UK garage management system with desktop software roots. Go Ops
          is a modern, cloud-native alternative built from the ground up with AI automation, a mobile-first
          interface, and real-time updates. No software installation required - just open your browser.
        </Typography>

        {/* Comparison Table */}
        <TableContainer component={Paper} sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#2c3e50' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Feature</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Go Ops</TableCell>
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>TechMan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comparisonData.map((row, index) => (
                <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f8f9fa' } }}>
                  <TableCell>{row.feature}</TableCell>
                  <TableCell align="center">
                    {row.goOps ? (
                      <CheckIcon sx={{ color: '#28a745' }} />
                    ) : (
                      <CloseIcon sx={{ color: '#dc3545' }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.competitor ? (
                      <CheckIcon sx={{ color: '#28a745' }} />
                    ) : (
                      <CloseIcon sx={{ color: '#dc3545' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Key Differences */}
        <Paper sx={{ p: 4, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
            Key Differences
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Cloud-Native:</strong> Go Ops runs entirely in the cloud - no software to install,
            no servers to maintain, no IT support needed. Access from any device with a browser.
            TechMan requires local installation and server setup.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Modern UI:</strong> Go Ops has a clean, modern interface designed for today&apos;s
            expectations. The mobile-first design means technicians can use it comfortably on phones
            and tablets in the workshop.
          </Typography>
          <Typography variant="body1">
            <strong>AI Automation:</strong> Go Ops uses AI to automatically extract booking details from
            emails, reducing manual data entry and the risk of errors. TechMan relies on manual input.
          </Typography>
        </Paper>

        {/* CTA */}
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Ready for a modern upgrade?
          </Typography>
          <Typography sx={{ mb: 3, color: '#666' }}>
            See how a cloud-native system transforms your workflow.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              component={RouterLink}
              to="/contact"
              sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e65100' } }}
            >
              Book a Demo
            </Button>
            <Button
              variant="outlined"
              component={RouterLink}
              to="/features"
              sx={{ borderColor: '#2c3e50', color: '#2c3e50' }}
            >
              View All Features
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#2c3e50', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Go Ops. All rights reserved.
            </Typography>
            <Stack direction="row" spacing={3}>
              <Button component={RouterLink} to="/features" sx={{ color: 'white', textTransform: 'none' }}>
                Features
              </Button>
              <Button component={RouterLink} to="/pricing" sx={{ color: 'white', textTransform: 'none' }}>
                Pricing
              </Button>
              <Button component={RouterLink} to="/contact" sx={{ color: 'white', textTransform: 'none' }}>
                Contact
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TechMan;
